import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { X } from 'lucide-react'

import { TagPicker } from '@/features/projects/components/tag-picker'
import { tagTone } from '@/features/projects/lib/tag-tone'
import {
  attachProjectTag,
  detachProjectTag,
  listProjectTags,
} from '@/features/projects/projects-api'
import { projectTagsQueryKey, projectsQueryKey } from '@/features/projects/projects-query-keys'
import { Badge } from '@/shared/components/ui/badge'
import type { Project } from '@/shared/types/domain'

interface ProjectTagsProps {
  project: Project
  emptyHint?: string
}

export function ProjectTags({ project, emptyHint }: ProjectTagsProps) {
  const queryClient = useQueryClient()
  // Теги принадлежат организации: у легаси-проекта без неё вешать их не на что.
  const canEdit = Boolean(project.organization_id)
  const tags = project.tags ?? []

  const directoryQuery = useQuery({
    enabled: canEdit,
    queryKey: projectTagsQueryKey,
    queryFn: listProjectTags,
  })

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: projectsQueryKey })
    void queryClient.invalidateQueries({ queryKey: projectTagsQueryKey })
    void queryClient.invalidateQueries({ queryKey: ['project', project.id] })
  }

  const attachMutation = useMutation({
    mutationFn: (payload: { name: string } | { tag_id: string }) =>
      attachProjectTag(project.id, payload),
    onSuccess: invalidate,
  })

  const detachMutation = useMutation({
    mutationFn: (tagId: string) => detachProjectTag(project.id, tagId),
    onSuccess: invalidate,
  })

  const busy = attachMutation.isPending || detachMutation.isPending

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {tags.map((tag) => (
        <Badge className="max-w-[12rem]" key={tag.id} title={tag.name} tone={tagTone(tag.name)}>
          <span className="truncate">{tag.name}</span>
          {canEdit ? (
            <button
              aria-label={`Убрать тег ${tag.name}`}
              className="ml-0.5 opacity-60 transition hover:opacity-100 disabled:opacity-30"
              disabled={busy}
              onClick={() => detachMutation.mutate(tag.id)}
              type="button"
            >
              <X className="h-3 w-3" />
            </button>
          ) : null}
        </Badge>
      ))}

      {tags.length === 0 && emptyHint ? (
        <span className="text-xs text-[var(--color-text-muted)]">{emptyHint}</span>
      ) : null}

      {canEdit ? (
        <TagPicker
          disabled={busy}
          onPick={(payload) => attachMutation.mutate(payload)}
          selectedIds={tags.map((tag) => tag.id)}
          tags={directoryQuery.data ?? []}
        />
      ) : null}
    </div>
  )
}
